import { forEach, sumBy, sum, findIndex, find } from 'lodash';
import stc from 'string-to-color';
import { ChartData } from '@scholastic/volume-react';

import { dateToUTC } from '../../utils/dataparsers.util';

export interface TransformChartDataResponse {
  totalChartobj: ChartData;
  onsiteChartObj: ChartData;
  remoteChartObj: ChartData;
  individualChartObj: ChartData;
}

export interface ChartApiRequest {
  request: string;
  cuid: string;
  startDate?: string;
  endDate?: string;
  product?: string[];
  customer?: string;
  [key: string]: any;
}

export interface CommonAPIResponse {
  result: object;
  resultcode: number;
}

export interface ChartDataBlock {
  documents: number;
  sessions: number;
  searches: number;
}

export interface ChartTabType {
  onsite: ChartDataBlock;
  remote: ChartDataBlock;
  individual: ChartDataBlock;
}

export interface ChartApiResponse extends CommonAPIResponse {
  result: { [key: string]: ChartTabType };
}

interface GroupChartType {
  [key: string]: ChartTabType;
}

export interface GroupedChartApiResponse extends CommonAPIResponse {
  result: { [key: string]: GroupChartType };
}

export interface IndividualSiteChartResponse extends ChartApiResponse {
  institution: string;
}

export interface ChartResponseComponent {
  name: string;
  value: number;
  color: string | undefined;
}

// sort chart data by date label
const sortChartDataByDate = (data: ChartData) => {
  data.sort(function (a, b) {
    let keyA = new Date(a.label + ',1'),
      keyB = new Date(b.label + ',1');
    // Compare the 2 dates
    if (keyA < keyB) return -1;
    if (keyA > keyB) return 1;
    return 0;
  });
  return data;
};

export const transformChartData = (
  rawCharttData: ChartApiResponse,
  reportType: string,
  productName: string,
): TransformChartDataResponse => {
  const totalChartobj: ChartData = [];
  const onsiteChartObj: ChartData = [];
  const remoteChartObj: ChartData = [];
  const individualChartObj: ChartData = [];

  forEach(rawCharttData.result, function (value, key) {
    let onsiteDocumentSum = sumBy([value.onsite], 'documents') || 0;
    let onsiteSsesionSum = sumBy([value.onsite], 'sessions') || 0;
    let onsiteSearchSum = sumBy([value.onsite], 'searches') || 0;

    let remoteDocumentSum = sumBy([value.remote], 'documents') || 0;
    let remoteSsesionSum = sumBy([value.remote], 'sessions') || 0;
    let remoteSearchSum = sumBy([value.remote], 'searches') || 0;

    let individualDocumentSum = sumBy([value.individual], 'documents') || 0;
    let individualSesionSum = sumBy([value.individual], 'sessions') || 0;
    let individualSearchSum = sumBy([value.individual], 'searches') || 0;

    const documentSum = sum([onsiteDocumentSum, remoteDocumentSum, individualDocumentSum]);
    const sesionSum = sum([onsiteSsesionSum, remoteSsesionSum, individualSesionSum]);
    const searchSum = sum([onsiteSearchSum, remoteSearchSum, individualSearchSum]);

    let objTotal: ChartResponseComponent[] = [];
    let objOnsite: ChartResponseComponent[] = [];
    let objRemote: ChartResponseComponent[] = [];
    let objIndividual: ChartResponseComponent[] = [];

    if (reportType === 'usageSummary') {
      objTotal = [
        {
          name: 'Documents',
          value: documentSum,
          color: '#56D76A',
        },
        {
          name: 'Sessions',
          color: '#FB3F26',
          value: sesionSum,
        },
      ];

      if (
        value.onsite.hasOwnProperty('searches') &&
        value.individual.hasOwnProperty('searches') &&
        value.remote.hasOwnProperty('searches')
      ) {
        objTotal.push({
          name: 'Searches',
          color: '#3C97EF',
          value: searchSum,
        });
      }

      objOnsite = [
        {
          name: 'Documents',
          value: onsiteDocumentSum,
          color: '#56D76A',
        },
        {
          name: 'Sessions',
          color: '#FB3F26',
          value: onsiteSsesionSum,
        },
      ];

      if (value.onsite.hasOwnProperty('searches')) {
        objOnsite.push({
          name: 'Searches',
          color: '#3C97EF',
          value: onsiteSearchSum,
        });
      }

      objRemote = [
        {
          name: 'Documents',
          value: remoteDocumentSum,
          color: '#56D76A',
        },
        {
          name: 'Sessions',
          color: '#FB3F26',
          value: remoteSsesionSum,
        },
      ];

      if (value.remote.hasOwnProperty('searches')) {
        objRemote.push({
          name: 'Searches',
          color: '#3C97EF',
          value: remoteSearchSum,
        });
      }

      objIndividual = [
        {
          name: 'Documents',
          value: individualDocumentSum,
          color: '#56D76A',
        },
        {
          name: 'Sessions',
          color: '#FB3F26',
          value: individualSesionSum,
        },
      ];

      if (value.individual.hasOwnProperty('searches')) {
        objIndividual.push({
          name: 'Searches',
          color: '#3C97EF',
          value: individualSearchSum,
        });
      }
    }

    if (reportType === 'documentUsage') {
      objTotal = [
        {
          name: productName,
          value: documentSum,
          color: '#56D76A',
        },
      ];

      objOnsite = [
        {
          name: productName,
          value: onsiteDocumentSum,
          color: '#56D76A',
        },
      ];

      objRemote = [
        {
          name: productName,
          value: remoteDocumentSum,
          color: '#56D76A',
        },
      ];

      objIndividual = [
        {
          name: productName,
          value: individualDocumentSum,
          color: '#56D76A',
        },
      ];
    }

    if (reportType === 'sessionUsage') {
      objTotal = [
        {
          name: productName,
          value: sesionSum,
          color: '#56D76A',
        },
      ];

      objOnsite = [
        {
          name: productName,
          value: onsiteSsesionSum,
          color: '#56D76A',
        },
      ];

      objRemote = [
        {
          name: productName,
          value: remoteSsesionSum,
          color: '#56D76A',
        },
      ];

      objIndividual = [
        {
          name: productName,
          value: individualSesionSum,
          color: '#56D76A',
        },
      ];
    }

    totalChartobj.push({
      label: dateToUTC(new Date(key)).toLocaleString('default', {
        month: 'short',
        year: 'numeric',
      }),
      dataset: objTotal,
    });

    onsiteChartObj.push({
      label: dateToUTC(new Date(key)).toLocaleString('default', {
        month: 'short',
        year: 'numeric',
      }),
      dataset: objOnsite,
    });

    remoteChartObj.push({
      label: dateToUTC(new Date(key)).toLocaleString('default', {
        month: 'short',
        year: 'numeric',
      }),
      dataset: objRemote,
    });

    individualChartObj.push({
      label: dateToUTC(new Date(key)).toLocaleString('default', {
        month: 'short',
        year: 'numeric',
      }),
      dataset: objIndividual,
    });
  });

  const completeChartResponsObj = {
    totalChartobj: sortChartDataByDate(totalChartobj),
    onsiteChartObj: sortChartDataByDate(onsiteChartObj),
    remoteChartObj: sortChartDataByDate(remoteChartObj),
    individualChartObj: sortChartDataByDate(individualChartObj),
  };

  return completeChartResponsObj;
};

export const transformIndividualChartData = (
  rawCharttData: IndividualSiteChartResponse[],
): TransformChartDataResponse => {
  const totalChartobj: ChartData = [];
  const onsiteChartObj: ChartData = [];
  const remoteChartObj: ChartData = [];
  const individualChartObj: ChartData = [];

  forEach(rawCharttData, (chartData) => {
    forEach(chartData.result, (value, key) => {
      let totalSum = 0;
      let onsiteSum = 0;
      let remoteSum = 0;
      let individualSum = 0;

      const objTotal: ChartResponseComponent[] = [];
      const objOnsite: ChartResponseComponent[] = [];
      const objRemote: ChartResponseComponent[] = [];
      const objIndividual: ChartResponseComponent[] = [];

      let onsiteDocumentSum = sumBy([value.onsite], 'documents') || 0;
      let onsiteSsesionSum = sumBy([value.onsite], 'sessions') || 0;
      let onsiteSearchSum = sumBy([value.onsite], 'searches') || 0;

      let remoteDocumentSum = sumBy([value.remote], 'documents') || 0;
      let remoteSsesionSum = sumBy([value.remote], 'sessions') || 0;
      let remoteSearchSum = sumBy([value.remote], 'searches') || 0;

      let individualDocumentSum = sumBy([value.individual], 'documents') || 0;
      let individualSesionSum = sumBy([value.individual], 'sessions') || 0;
      let individualSearchSum = sumBy([value.individual], 'searches') || 0;

      const documentSum = sum([onsiteDocumentSum, remoteDocumentSum, individualDocumentSum]);
      const sesionSum = sum([onsiteSsesionSum, remoteSsesionSum, individualSesionSum]);
      const searchSum = sum([onsiteSearchSum, remoteSearchSum, individualSearchSum]);

      totalSum = documentSum + sesionSum + searchSum;
      onsiteSum = onsiteDocumentSum + onsiteSsesionSum + onsiteSearchSum;
      remoteSum = remoteDocumentSum + remoteSsesionSum + remoteSearchSum;
      individualSum = individualDocumentSum + individualSesionSum + individualSearchSum;

      objTotal.push({
        name: chartData.institution,
        value: totalSum,
        color: generateHexCode(chartData.institution),
      });

      objOnsite.push({
        name: chartData.institution,
        value: onsiteSum,
        color: generateHexCode(chartData.institution),
      });

      objRemote.push({
        name: chartData.institution,
        value: remoteSum,
        color: generateHexCode(chartData.institution),
      });

      objIndividual.push({
        name: chartData.institution,
        value: individualSum,
        color: generateHexCode(chartData.institution),
      });

      if (
        !find(totalChartobj, [
          'label',
          dateToUTC(new Date(key)).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
        ])
      ) {
        totalChartobj.push({
          label: dateToUTC(new Date(key)).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
          dataset: objTotal,
        });
      } else {
        let totalChartObjIndex = findIndex(totalChartobj, [
          'label',
          dateToUTC(new Date(key)).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
        ]);
        totalChartobj[totalChartObjIndex].dataset.push(...objTotal);
      }

      if (
        !find(onsiteChartObj, [
          'label',
          dateToUTC(new Date(key)).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
        ])
      ) {
        onsiteChartObj.push({
          label: dateToUTC(new Date(key)).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
          dataset: objOnsite,
        });
      } else {
        let onsiteChartObjIndex = findIndex(onsiteChartObj, [
          'label',
          dateToUTC(new Date(key)).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
        ]);
        onsiteChartObj[onsiteChartObjIndex].dataset.push(...objOnsite);
      }

      if (
        !find(remoteChartObj, [
          'label',
          dateToUTC(new Date(key)).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
        ])
      ) {
        remoteChartObj.push({
          label: dateToUTC(new Date(key)).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
          dataset: objRemote,
        });
      } else {
        let remoteChartObjIndex = findIndex(remoteChartObj, [
          'label',
          dateToUTC(new Date(key)).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
        ]);
        remoteChartObj[remoteChartObjIndex].dataset.push(...objRemote);
      }

      if (
        !find(individualChartObj, [
          'label',
          dateToUTC(new Date(key)).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
        ])
      ) {
        individualChartObj.push({
          label: dateToUTC(new Date(key)).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
          dataset: objIndividual,
        });
      } else {
        let individualChartObjIndex = findIndex(individualChartObj, [
          'label',
          dateToUTC(new Date(key)).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
        ]);
        individualChartObj[individualChartObjIndex].dataset.push(...objIndividual);
      }
    });
  });

  const completeChartResponsObj = {
    totalChartobj: sortChartDataByDate(totalChartobj),
    onsiteChartObj: sortChartDataByDate(onsiteChartObj),
    remoteChartObj: sortChartDataByDate(remoteChartObj),
    individualChartObj: sortChartDataByDate(individualChartObj),
  };

  return completeChartResponsObj;
};

export const transformGroupedChartData = (rawCharttData: any): TransformChartDataResponse => {
  const totalChartobj: ChartData = [];
  const onsiteChartObj: ChartData = [];
  const remoteChartObj: ChartData = [];
  const individualChartObj: ChartData = [];

  Object.entries(rawCharttData.result).map((chartData: [string, any]) => {
    Object.entries(chartData[1]).map((value: [string, any]) => {
      let totalSum = 0;
      let onsiteSum = 0;
      let remoteSum = 0;
      let individualSum = 0;

      const objTotal: ChartResponseComponent[] = [];
      const objOnsite: ChartResponseComponent[] = [];
      const objRemote: ChartResponseComponent[] = [];
      const objIndividual: ChartResponseComponent[] = [];

      let onsiteDocumentSum = sumBy([value[1].onsite], 'documents') || 0;
      let onsiteSsesionSum = sumBy([value[1].onsite], 'sessions') || 0;

      let remoteDocumentSum = sumBy([value[1].remote], 'documents') || 0;
      let remoteSsesionSum = sumBy([value[1].remote], 'sessions') || 0;

      let individualDocumentSum = sumBy([value[1].individual], 'documents') || 0;
      let individualSesionSum = sumBy([value[1].individual], 'sessions') || 0;

      const documentSum = sum([onsiteDocumentSum, remoteDocumentSum, individualDocumentSum]);
      const sesionSum = sum([onsiteSsesionSum, remoteSsesionSum, individualSesionSum]);

      totalSum = documentSum + sesionSum;
      onsiteSum = onsiteDocumentSum + onsiteSsesionSum;
      remoteSum = remoteDocumentSum + remoteSsesionSum;
      individualSum = individualDocumentSum + individualSesionSum;

      objTotal.push({
        name: chartData[0],
        value: totalSum,
        color: generateHexCode(chartData[0]),
      });

      objOnsite.push({
        name: chartData[0],
        value: onsiteSum,
        color: generateHexCode(chartData[0]),
      });

      objRemote.push({
        name: chartData[0],
        value: remoteSum,
        color: generateHexCode(chartData[0]),
      });

      objIndividual.push({
        name: chartData[0],
        value: individualSum,
        color: generateHexCode(chartData[0]),
      });

      if (
        !find(totalChartobj, [
          'label',
          dateToUTC(new Date(value[0])).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
        ])
      ) {
        totalChartobj.push({
          label: dateToUTC(new Date(value[0])).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
          dataset: objTotal,
        });
      } else {
        let totalChartObjIndex = findIndex(totalChartobj, [
          'label',
          dateToUTC(new Date(value[0])).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
        ]);
        totalChartobj[totalChartObjIndex].dataset.push(...objTotal);
      }

      if (
        !find(onsiteChartObj, [
          'label',
          dateToUTC(new Date(value[0])).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
        ])
      ) {
        onsiteChartObj.push({
          label: dateToUTC(new Date(value[0])).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
          dataset: objOnsite,
        });
      } else {
        let onsiteChartObjIndex = findIndex(onsiteChartObj, [
          'label',
          dateToUTC(new Date(value[0])).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
        ]);
        onsiteChartObj[onsiteChartObjIndex].dataset.push(...objOnsite);
      }

      if (
        !find(remoteChartObj, [
          'label',
          dateToUTC(new Date(value[0])).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
        ])
      ) {
        remoteChartObj.push({
          label: dateToUTC(new Date(value[0])).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
          dataset: objRemote,
        });
      } else {
        let remoteChartObjIndex = findIndex(remoteChartObj, [
          'label',
          dateToUTC(new Date(value[0])).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
        ]);
        remoteChartObj[remoteChartObjIndex].dataset.push(...objRemote);
      }

      if (
        !find(individualChartObj, [
          'label',
          dateToUTC(new Date(value[0])).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
        ])
      ) {
        individualChartObj.push({
          label: dateToUTC(new Date(value[0])).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
          dataset: objIndividual,
        });
      } else {
        let individualChartObjIndex = findIndex(individualChartObj, [
          'label',
          dateToUTC(new Date(value[0])).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
        ]);
        individualChartObj[individualChartObjIndex].dataset.push(...objIndividual);
      }
      return {};
    });
    return {};
  });

  const completeChartResponsObj = {
    totalChartobj: sortChartDataByDate(totalChartobj),
    onsiteChartObj: sortChartDataByDate(onsiteChartObj),
    remoteChartObj: sortChartDataByDate(remoteChartObj),
    individualChartObj: sortChartDataByDate(individualChartObj),
  };

  return completeChartResponsObj;
};

export const serializeQuery = (params: any, prefix: string | undefined = ''): string => {
  const query = Object.keys(removeEmpty(params)).map((key, index) => {
    const value = params[key];

    if (params instanceof Array) {
      key = `${prefix}[${index}]`;
    } else if (params instanceof Object) {
      key = prefix ? `${prefix}[${key}]` : key;
    }

    if (typeof value === 'object') {
      return serializeQuery(value, key);
    } else {
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    }
  });

  return ([] as string[]).concat.apply([], query).join('&');
};

const removeEmpty = (obj: any) => {
  let newObj: any = {};
  Object.keys(obj).forEach((key) => {
    if (obj[key] === Object(obj[key])) newObj[key] = removeEmpty(obj[key]);
    else if (obj[key] !== undefined) newObj[key] = obj[key];
  });
  return newObj;
};

//Generate Hex color code based on String for consistency
const generateHexCode = (str: string) => {
  return stc(str);
};
